import React, { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { X, ChevronLeft, ChevronRight, Loader2, History as HistoryIcon } from "lucide-react";
import { getIncomeHistory } from "@/lib/api";

const usd2 = (n) =>
  new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

const TABS = [
  { key: "all", label: "All" },
  { key: "roi", label: "ROI Income" },
  { key: "level", label: "Level Income" },
  { key: "daily", label: "Daily Pool" },
  { key: "weekly", label: "Weekly Pool" },
  { key: "monthly", label: "Monthly Pool" },
  { key: "top", label: "Top Depositor" },
];

const CAT_META = {
  direct: { label: "Direct Reward", color: "#7CF3A0" },
  roi: { label: "ROI Income", color: "#0AA84F" },
  level: { label: "Level Income", color: "#FFA000" },
  daily: { label: "Daily Pool", color: "#65B82E" },
  weekly: { label: "Weekly Pool", color: "#D6C51E" },
  monthly: { label: "Monthly Pool", color: "#12B76A" },
  top: { label: "Top Depositor", color: "#FF7A1A" },
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "UTC",
    }) + " UTC";
  } catch { return iso; }
};

export default function IncomeHistory({ address, onClose }) {
  const [tab, setTab] = useState("all");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!address) return;
    setLoading(true);
    try {
      setData(await getIncomeHistory(address, tab, page));
    } catch (e) {
      setData({ rows: [], page: 1, pages: 1, total: 0 });
    } finally {
      setLoading(false);
    }
  }, [address, tab, page]);

  useEffect(() => { load(); }, [load]);

  const rows = data?.rows || [];
  const pages = data?.pages || 1;
  const total = data?.total || 0;

  return createPortal(
    <div
      data-testid="income-history-modal"
      className="fixed inset-0 z-[200] flex items-end justify-center bg-black/80 backdrop-blur-sm sm:items-center"
      onClick={onClose}
    >
      <div
        className="flex h-[94vh] w-full max-w-[440px] flex-col overflow-hidden rounded-t-3xl border border-[#3C6B33]/60 bg-[#0A1710] shadow-2xl sm:h-[92vh] sm:rounded-3xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-white/10 px-5 py-4">
          <span className="flex items-center gap-2 text-base font-bold grad-title">
            <HistoryIcon size={17} className="text-[#34D07A]" /> Income History
          </span>
          <button
            data-testid="income-history-close"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-full bg-white/5 text-white/60 active:scale-95"
          >
            <X size={16} />
          </button>
        </div>

        {/* Tabs (horizontal scroll) */}
        <div className="no-scrollbar flex gap-2 overflow-x-auto border-b border-white/10 px-4 py-3">
          {TABS.map((t) => (
            <button
              key={t.key}
              data-testid={`income-tab-${t.key}`}
              onClick={() => { setTab(t.key); setPage(1); }}
              className={`shrink-0 whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors ${
                tab === t.key
                  ? "bg-gradient-to-r from-[#0AA84F] to-[#D6C51E] text-black"
                  : "border border-white/12 bg-white/5 text-white/60"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* List */}
        <div className="min-h-[240px] flex-1 overflow-y-auto px-4 py-3">
          {loading ? (
            <div className="flex h-40 items-center justify-center text-white/40">
              <Loader2 size={22} className="animate-spin" />
            </div>
          ) : rows.length === 0 ? (
            <div className="flex h-40 flex-col items-center justify-center gap-1 text-center text-sm text-white/40">
              <HistoryIcon size={26} className="text-white/20" />
              No income records yet
            </div>
          ) : (
            <div className="flex flex-col gap-1.5">
              {rows.map((r, i) => {
                const m = CAT_META[r.category] || { label: r.category, color: "#34D07A" };
                return (
                  <div
                    key={i}
                    data-testid={`income-row-${i}`}
                    className="flex items-center justify-between rounded-lg border border-[#3C6B33]/40 bg-white/[0.02] px-3 py-2"
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5 text-[13px] font-semibold text-white">
                        <span className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: m.color }} />
                        {m.label}
                      </div>
                      <div className="mt-0.5 text-[10px] text-white/40">{fmtDate(r.created_at)}</div>
                    </div>
                    <div className="shrink-0 text-sm font-bold text-[#34D07A]">+${usd2(r.amount_usd)}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between border-t border-white/10 px-4 py-3">
          <button
            data-testid="income-prev-btn"
            disabled={page <= 1 || loading}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="flex items-center gap-1 rounded-lg border border-white/12 px-3 py-2 text-xs font-semibold text-white/70 disabled:opacity-40"
          >
            <ChevronLeft size={14} /> Previous
          </button>
          <span data-testid="income-page-indicator" className="text-[11px] text-white/45">
            Page {data?.page || 1} / {pages} · {total} total
          </span>
          <button
            data-testid="income-next-btn"
            disabled={page >= pages || loading}
            onClick={() => setPage((p) => Math.min(pages, p + 1))}
            className="flex items-center gap-1 rounded-lg border border-[#0AA84F]/40 bg-[#0AA84F]/10 px-3 py-2 text-xs font-semibold text-[#34D07A] disabled:opacity-40"
          >
            Next <ChevronRight size={14} />
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
